import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { clientContent } from '../../client/content';
import Section from '../../components/Section';
import CtaBlock from '../../components/CtaBlock';
import HowItWorks from '../../components/HowItWorks';
import styles from './Home.module.scss';
import tradesImg from '../../assets/images/trades.webp';
import consultantImg from '../../assets/images/consultant.webp';
import professionalImg from '../../assets/images/professional.webp';
import heroMockupImg from '../../assets/images/website-display-on-mobile-and-desktop.webp';

const Home: React.FC = () => {
  const { title, description, canonical } = clientContent.seo.home;
  const { hero, audience, themes, servicesSummary, featuredDemos } = clientContent.home;

  const heroFeatures = [
    {
      title: "Fast turnaround",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
      ),
    },
    {
      title: "Mobile-compatible",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
      ),
    },
    {
      title: "Hosted, maintained & kept secure",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
      ),
    },
    {
      title: "No technical hassle",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
      ),
    },
    {
      title: "Rapid loading",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
      ),
    },
  ];

  return (
    <div className={styles.home} data-testid="home-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={`container ${styles.heroContainer}`}>
          <div className={styles.heroContent}>
            <span className={styles.eyebrow}>{hero.eyebrow}</span>
            <h1 className={styles.title}>{hero.title}</h1>
            <p className={styles.subtitle}>{hero.subtitle}</p>
            <div className={styles.heroFeatures}>
              {heroFeatures.map((feature, index) => (
                <div key={index} className={styles.heroFeatureItem}>
                  <span className={styles.featureIcon}>{feature.icon}</span>
                  <span className={styles.featureText}>{feature.title}</span>
                </div>
              ))}
            </div>
            <div className={styles.actions}>
              <Link to={hero.primaryCta.path} className={styles.primaryBtn}>
                {hero.primaryCta.label}
              </Link>
              <Link to={hero.secondaryCta.path} className={styles.secondaryBtn}>
                {hero.secondaryCta.label}
              </Link>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <img 
              src={heroMockupImg} 
              alt="Website mockup displayed on laptop and mobile devices" 
              className={styles.heroImage}
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <Section background="surface">
        <div className={styles.audience}>
          <h2>{audience.heading}</h2>
          <p className={styles.audienceDesc}>{audience.description}</p>
          <div className={styles.painPointsGrid}>
            {audience.painPoints.map((point, index) => (
              <div key={index} className={styles.painPointCard}>
                <span className={styles.checkIcon}>✓</span>
                <p>{point}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Core Themes Section */}
      <Section background="default">
        <div className={styles.sectionHeader}>
          <h2>The Clockwork Web Promise</h2>
          <p>Built around three core principles to give you complete peace of mind.</p>
        </div>
        <div className={styles.themesGrid}>
          {themes.map((theme, index) => (
            <div key={index} className={styles.themeCard}>
              <span className={styles.themeBadge}>0{index + 1}</span>
              <h3>{theme.title}</h3>
              <p>{theme.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Service Summary Section */}
      <Section background="surface">
        <div className={styles.sectionHeader}>
          <h2>What We Take Care Of</h2>
          <p>Everything your business needs to establish and maintain a credible online presence.</p>
        </div>
        <div className={styles.servicesGrid}>
          {servicesSummary.map((service, index) => (
            <div key={index} className={styles.serviceCard}>
              <div className={styles.icon}>
                {index === 0 && (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                )}
                {index === 1 && (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path></svg>
                )}
                {index === 2 && (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                )}
                {index === 3 && (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                )}
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <Link to="/what-we-do" className={styles.serviceLink}>
                Learn more →
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* Selected Demos Section */}
      <Section background="default">
        <div className={styles.sectionHeader}>
          <h2>Example Websites</h2>
          <p>Explore representative demo websites showcasing our standard of design and responsiveness.</p>
        </div>
        <div className={styles.demosGrid}>
          {featuredDemos.map((demo) => {
            return (
              <Link 
                key={demo.id} 
                to="/concepts" 
                style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
              >
                <div className={styles.demoCard} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div className={styles.demoPreview}>
                    <img 
                      src={demo.id === 'trades' ? tradesImg : demo.id === 'consulting' ? consultantImg : professionalImg} 
                      alt={demo.title} 
                      className={styles.demoImg}
                    />
                  </div>
                  <div className={styles.demoContent}>
                    <h3>{demo.title}</h3>
                    <p>{demo.description}</p>
                    <span className={styles.serviceLink}>
                      View Concepts →
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        <div className={styles.centerAction}>
          <Link to="/concepts" className={styles.examplesBtn}>
            Explore Concepts
          </Link>
        </div>
      </Section>

      {/* Process Overview Section */}
      <HowItWorks
        title="How It Works"
        subtitle="A straightforward, stress-free process designed to get your business online."
        background="surface"
      />

      {/* Final Enquiry CTA */}
      <Section background="default">
        <CtaBlock
          title="Ready for a professional website without the hassle?"
          subtitle="Get in touch today to discuss your business and start your new website."
          buttonText="Get in Touch"
          buttonPath="/contact"
          secondaryButtonText="Explore FAQ"
          secondaryButtonPath="/faq"
        />
      </Section>
    </div>
  );
};

export default Home;
